import { useMemo } from 'react';
import { miktarMetni } from './bicim';
import { SectionCard, fmtTRY, fmtNum } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';
import { KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';

interface CokSatan {
  stok_kodu: string;
  urun_adi: string;
  ciro: number;
  miktar: number;
}
interface KritikStok {
  stok_kodu: string;
  urun: string;
  eldeki_miktar: number;
  gunluk_satis: number;
  kalan_gun: number;
}

export interface Risk {
  kod: string;
  ad: string;
  ciro: number;
  eldeki: number;
  gunlukSatis: number;
  kalanGun: number;
}

interface Props {
  /** 990014 + 990025. G2b: sayfa cozer (014 SQL 142 sonrasi ekran sozlugunden). */
  kaynaklar: Pick<SatisKaynaklari, 'cokSatan014' | 'kritik025'>;
  donem: Donem;
  sube?: string | null;
  /**
   * G2: stok kanal bagimsiz; kanal SUZULMEZ. G2b/S3 fix1: kapsam notu ("kanal süzgeci uygulanmaz") artik
   * BLOKTA yazilmiyor — TiklaAc basligindaki Bilgi'ye tasindi (pages/SatisAnaliz.tsx, yalniz kanal
   * seciliyken); burada tekrar basilirsa CIFT gorunurdu.
   */
  kanal?: string | null;
}

/**
 * İKİ raporun KESISIMI: donemde satan urunler (014) ∩ eldeki stogu tukenmek uzere
 * olanlar (025). Kesisim SART - yalnizca "stok az" listesi hicbir sey soylemez
 * (hic satmayan bir urunun stogunun az olmasi normaldir), yalnizca "cok satan"
 * listesi de oyle. Riskli olan IKISININ AYNI ANDA dogru oldugu urundur.
 *
 * Anahtar `stok_kodu` — iki rapor da onu donduruyor. Urun ADIYLA eslestirmek
 * ayni ada sahip iki karti birlestirirdi.
 *
 * Siralama `kalan_gun` ARTAN (esitlikte donem cirosu azalan): once rafi en cabuk
 * bosalacak olan. Bu blokta ESIK YOK — "kac gun kritik" diye bir sayi uydurulmaz;
 * kesisimdeki olculebilir her urun (kalan_gun > 0) sirali listeye girer, ekranda
 * ilk 12'si gosterilir ve kirpma yazilir.
 */
export function riskliUrunler(cokSatan: CokSatan[], kritik: KritikStok[]): Risk[] {
  const ciroMap = new Map<string, CokSatan>();
  for (const c of cokSatan) ciroMap.set(String(c.stok_kodu), c);

  const sonuc: Risk[] = [];
  for (const k of kritik) {
    const kod = String(k.stok_kodu);
    const c = ciroMap.get(kod);
    if (!c) continue; // donemde satmamis -> "satiyor ama bitiyor" degil
    const kalanGun = Number(k.kalan_gun ?? 0);
    // `kalan_gun` NULL/0 gelebilir (gunluk satis 0'a bolunmus): o satir bir risk
    // OLCUSU tasimiyor, listeye alinmaz - 0 gun gostermek "bugun bitiyor" gibi
    // okunur ve YANLIS alarm uretir.
    if (!Number.isFinite(kalanGun) || kalanGun <= 0) continue;
    sonuc.push({
      kod,
      ad: String(k.urun ?? c.urun_adi ?? ''),
      ciro: Number(c.ciro ?? 0),
      eldeki: Number(k.eldeki_miktar ?? 0),
      gunlukSatis: Number(k.gunluk_satis ?? 0),
      kalanGun,
    });
  }
  return sonuc.sort((a, b) => a.kalanGun - b.kalanGun || b.ciro - a.ciro);
}

export function StokSatisBlok({ kaynaklar, donem, sube = null }: Props) {
  const kaynakCokSatan = kaynaklar.cokSatan014;
  const kaynakKritik = kaynaklar.kritik025;

  // Kanal BILEREK gonderilmez (spec §8: stok kanal bagimsiz); 014 @LOKASYON bildiriyorsa sube gider.
  const cokSatanQ = useSaSorgu<CokSatan>(kaynakCokSatan, { donem, sube, kanal: null });
  // 🔴 025 @LOKASYON ALMIYOR (canlida olculdu); stok tarafi firma geneli kalir ve ekranda yazilir.
  const kritikQ = useSaSorgu<KritikStok>(kaynakKritik, { donem, sube: null, kanal: null });

  const riskli = useMemo(
    () => riskliUrunler(cokSatanQ.data?.data ?? [], kritikQ.data?.data ?? []),
    [cokSatanQ.data, kritikQ.data],
  );

  if (!kaynakCokSatan || !kaynakKritik) return null; // iki kaynaktan biri yoksa kesisim kurulamaz
  if (cokSatanQ.isLoading || kritikQ.isLoading)
    return (
      <BlokIskeleti
        testid="blok-stok-iskelet"
        altBaslik="Dönemde satan ürünlerden rafı en çabuk boşalacaklar"
        yukseklik={280}
        satir={5}
      />
    );
  if (riskli.length === 0) return null; // bos durum kurali

  return (
    <div data-testid="blok-stok-satis">
      <SectionCard
        // Baslik BASILMAZ: TiklaAc kabugu ayni metni bir kez zaten gosteriyor (bkz. TiklaAc.tsx).
        subtitle={
          <>
            Dönemde satan ürünlerden rafı en çabuk boşalacaklar — eşiksiz, sıralı
            <KapsamNotu sube={sube} destekli={false} />
          </>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-ink-400 text-left">
                <th className="font-normal py-1">Ürün</th>
                <th className="font-normal py-1 text-right">Dönem cirosu</th>
                <th className="font-normal py-1 text-right">Eldeki</th>
                <th className="font-normal py-1 text-right">Günlük satış</th>
                <th className="font-normal py-1 text-right">Kalan gün</th>
              </tr>
            </thead>
            <tbody>
              {riskli.slice(0, 12).map((r) => (
                <tr key={r.kod} data-testid={`stok-satir-${r.kod}`} className="border-t border-border">
                  <td className="py-1 pr-2 text-ink-800 truncate">{r.ad}</td>
                  <td className="py-1 text-right tnum text-ink-700">{fmtTRY(r.ciro)}</td>
                  <td className="py-1 text-right tnum text-ink-700">{miktarMetni(r.eldeki)}</td>
                  <td className="py-1 text-right tnum text-ink-700">{miktarMetni(r.gunlukSatis)}</td>
                  <td className="py-1 text-right tnum font-medium text-neg-700">{miktarMetni(r.kalanGun)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {riskli.length > 12 && (
            <div className="text-[11px] text-ink-400 pt-1.5" data-testid="stok-kirpma-notu">
              İlk 12 gösteriliyor — toplam {fmtNum(riskli.length)} ürün
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
